import React from 'react';

import { Desktop, Mobile } from 'components/Responsive';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { Airport } from 'models/airport';
import { FlightGroupedResultResponse } from 'models/flightSearch';
import { getFormattedPrice } from 'utils/currency';

import FlightOption from './FlightOption';
import {
  Card,
  FlightsContainer,
  Summary,
  RouteTypeWrapper,
  RouteTypeText,
  RouteTypeRoot,
  RouteTypeDate,
  RouteTypeHeaderContainer,
  RouteTypeHeaderText,
  FlightsListContainer,
  FinalPriceWithTaxes,
  PriceContainer,
  PriceWrapper,
  Price,
  Currency,
  ConfirmButton,
  FlightsWrapper,
  RouteTypeStartEndAirportContainer,
  RouteTypeCityName,
  SummaryMobile,
  ConfirmButtomMobile,
  FinalPriceWithTaxesMobile,
  BreakRulesMessage,
  NoBreakRulesMessage,
  ConfirmUpSellButton,
  ConfirmUpSellButtomMobile,
  FooterMobile,
} from './styles';
import { getFormattedRouteType } from './utils';

interface Props {
  data: FlightGroupedResultResponse;
  onClickWithUpSell: (selectedFlightsKeys: [string, string]) => void;
  testId?: string;
  departurePoint?: Airport;
  arrivalPoint?: Airport;
  departureDate?: Date;
  returnDate?: Date;
  onClickWithoutUpSell: (selectedFlightsKeys: [string, string]) => void;
}

const RouteType = ({ label, date }) => (
  <RouteTypeRoot>
    <RouteTypeWrapper>
      <img alt="flight-duotone" src="/images/flight-duotone.svg" />
      <RouteTypeText>{label}</RouteTypeText>
    </RouteTypeWrapper>
    <RouteTypeDate>{date}</RouteTypeDate>
  </RouteTypeRoot>
);

const RouteTypeStartEndAirport = ({ airport }: { airport?: Airport }) => (
  <RouteTypeStartEndAirportContainer>
    <RouteTypeHeaderText>{airport?.code}</RouteTypeHeaderText>
    <RouteTypeCityName title={airport?.related_city}>{airport?.related_city}</RouteTypeCityName>
  </RouteTypeStartEndAirportContainer>
);

const RouteTypeHeader = ({
  routeType,
  date,
  airports: [departureAirport, arrivalAirport],
}: {
  routeType: 'Ida' | 'Vuelta';
  date: string;
  airports: [Airport?, Airport?];
}) => {
  const { t } = useStoreTranslation();

  return (
    <RouteTypeHeaderContainer>
      <RouteType label={routeType} date={date} />
      <Desktop>
        <RouteTypeStartEndAirport airport={departureAirport} />
        <div />
        <RouteTypeStartEndAirport airport={arrivalAirport} />
        <RouteTypeHeaderText>{t('flightsCard.luggage')}</RouteTypeHeaderText>
      </Desktop>
    </RouteTypeHeaderContainer>
  );
};

const CardGroupedFlights = ({
  data,
  onClickWithUpSell,
  testId,
  arrivalPoint,
  departureDate,
  departurePoint,
  returnDate,
  onClickWithoutUpSell,
}: Props) => {
  const { t } = useStoreTranslation();
  const {
    sectors: [departureFlights, returnFlights],
    price: { total, currencyCode } = {
      total: 0,
      currencyCode: 'BRL',
    },
    breakRulesMessage,
  } = data || {};
  const [selectedFlightKeys, setSelectedFlightKeys] = React.useState<[string, string]>([
    departureFlights?.options.first().flightKeys,
    returnFlights?.options.first().flightKeys,
  ]);

  const formattedPrice = (value: number) => getFormattedPrice({ value, currency: currencyCode, t });
  const formattedPriceTotal = formattedPrice(total);

  const [departureSelectedFlight, returnSelectedFlight] = selectedFlightKeys;

  const handleSelectedFlightKeys = (type: 'departure' | 'return') => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (type === 'departure') {
      setSelectedFlightKeys([value, returnSelectedFlight]);
    } else {
      setSelectedFlightKeys([departureSelectedFlight, value]);
    }
  };

  const handleSelectFlightWithoutUpSell = () => {
    onClickWithoutUpSell((selectedFlightKeys.map((s) => s?.replaceAll('#', ',')) as unknown) as [string, string]);
  };

  const handleSelectFlightWithUpSell = () =>
    onClickWithUpSell((selectedFlightKeys.map((s) => s?.replaceAll('#', ',')) as unknown) as [string, string]);

  return (
    <Card data-testid={testId}>
      <FlightsContainer>
        <FlightsWrapper>
          <RouteTypeHeader
            routeType={t('flightsCard.routeTypeDeparture')}
            date={getFormattedRouteType(departureDate)}
            airports={[departurePoint, arrivalPoint]}
          />
          <FlightsListContainer value={departureSelectedFlight} onChange={handleSelectedFlightKeys('departure')}>
            {departureFlights?.options?.map((option) => {
              const { flightKeys } = option;
              return <FlightOption key={flightKeys} groupedSectorOption={option} />;
            })}
          </FlightsListContainer>
        </FlightsWrapper>
        {returnFlights && returnFlights?.options && (
          <FlightsWrapper>
            <RouteTypeHeader
              routeType={t('flightsCard.routeTypeReturn')}
              date={getFormattedRouteType(returnDate)}
              airports={[arrivalPoint, departurePoint]}
            />
            <FlightsListContainer value={returnSelectedFlight} onChange={handleSelectedFlightKeys('return')}>
              {returnFlights?.options?.map((option) => {
                const { flightKeys } = option;
                return <FlightOption key={flightKeys} groupedSectorOption={option} />;
              })}
            </FlightsListContainer>
          </FlightsWrapper>
        )}
      </FlightsContainer>
      <Desktop>
        <Summary>
          <PriceContainer>
            <PriceWrapper>
              <Price>{formattedPriceTotal?.formattedWithoutCode}</Price>
              <Currency>{formattedPriceTotal?.currency}</Currency>
            </PriceWrapper>
          </PriceContainer>
          <FinalPriceWithTaxes>{t('flightsCard.finalPriceWithTaxes')}</FinalPriceWithTaxes>
          {breakRulesMessage && breakRulesMessage?.length > 0 ? (
            <BreakRulesMessage>{t('flightDetails.outOfPolicies')}</BreakRulesMessage>
          ) : (
            <NoBreakRulesMessage>{t('flightDetails.withinPolicies')}</NoBreakRulesMessage>
          )}
          <ConfirmButton onClick={handleSelectFlightWithoutUpSell}>{t('flightsCard.confirmButton')}</ConfirmButton>
          {data.upSellEnabled && (
            <ConfirmUpSellButton value={t('flightsCard.confirmUpSellButton')} onClick={handleSelectFlightWithUpSell} />
          )}
        </Summary>
      </Desktop>
      <Mobile>
        <SummaryMobile>
          <PriceContainer>
            <PriceWrapper>
              <Price>{formattedPriceTotal?.formattedWithoutCode}</Price>
              <Currency>{formattedPriceTotal?.currency}</Currency>
            </PriceWrapper>
            <FinalPriceWithTaxesMobile>{t('flightsCard.finalPriceWithTaxes')}</FinalPriceWithTaxesMobile>
            {breakRulesMessage && breakRulesMessage?.length > 0 ? (
              <BreakRulesMessage>{t('flightDetails.outOfPolicies')}</BreakRulesMessage>
            ) : (
              <NoBreakRulesMessage>{t('flightDetails.withinPolicies')}</NoBreakRulesMessage>
            )}
          </PriceContainer>
          <ConfirmButtomMobile onClick={handleSelectFlightWithoutUpSell}>
            {t('flightsCard.confirmButton')}
          </ConfirmButtomMobile>
        </SummaryMobile>
        {data.upSellEnabled && (
          <FooterMobile>
            <ConfirmUpSellButtomMobile
              value={t('flightsCard.confirmUpSellButton')}
              onClick={handleSelectFlightWithUpSell}
            />
          </FooterMobile>
        )}
      </Mobile>
    </Card>
  );
};

export default CardGroupedFlights;
